import { Link } from 'react-router-dom';
import { ErrorState, LoadingState } from '../components/StatePanel';
import { VideoEmbed } from '../components/VideoEmbed';
import { resolveAssetUrl } from '../config/appConfig';
import { useSiteConfig } from '../context/SiteConfigContext';
import { formatDate } from '../utils/formatDate';
import styles from './HomePage.module.css';

/**
 * A date as it should be read, falling back to however it was written.
 *
 * A date the site cannot parse is still a date somebody sat down and entered.
 * Showing it looks wrong; hiding it looks like nobody ever bothered, which is
 * worse and gives no hint that anything needs correcting.
 */
function shown(value: string): string {
  return formatDate(value) || value.trim();
}

/** "1 January 1940 – 2 February 2020", or an empty string when no dates are known. */
function formatLifeDates(dateOfBirth: string, dateOfDeath: string): string {
  const born = shown(dateOfBirth);
  const died = shown(dateOfDeath);
  if (born && died) return `${born} – ${died}`;
  return born || died;
}

export function HomePage() {
  const { siteConfig, status, error, reload } = useSiteConfig();

  if (status === 'loading') {
    return <LoadingState label="Loading the memorial…" />;
  }

  if (status === 'error' || siteConfig === null) {
    return <ErrorState message={error ?? 'The memorial details could not be loaded.'} onRetry={reload} />;
  }

  const lifeDates = formatLifeDates(siteConfig.dateOfBirth, siteConfig.dateOfDeath);

  return (
    <div className="container">
      <section className={styles.hero}>
        <figure className={styles.portrait}>
          {siteConfig.mainPhoto ? (
            <img
              src={resolveAssetUrl(siteConfig.mainPhoto)}
              alt={`${siteConfig.name}, remembered here`}
            />
          ) : (
            <div className={styles.portraitPlaceholder}>
              <p>The main photograph has not been added yet.</p>
            </div>
          )}
        </figure>

        <div>
          <span className={styles.eyebrow}>{siteConfig.title}</span>
          <h1 className={styles.name}>{siteConfig.name}</h1>
          {lifeDates ? (
            <p className={styles.dates}>{lifeDates}</p>
          ) : (
            <p className={styles.dates}>Dates to be added.</p>
          )}
          <p className={styles.welcome}>{siteConfig.welcomeText}</p>
        </div>
      </section>

      <VideoEmbed url={siteConfig.youtubeUrl} label={`A video remembering ${siteConfig.name}`} />

      <section aria-labelledby="explore-heading">
        <h2 id="explore-heading" className="visually-hidden">
          Explore this memorial
        </h2>

        <div className={styles.linkGrid}>
          <Link to="/photos" className={`card ${styles.linkCard}`}>
            <h2>Photographs</h2>
            <p>Look through photographs shared by family and friends.</p>
            <span className={styles.linkCardAction}>View the photographs</span>
          </Link>

          <Link to="/memories" className={`card ${styles.linkCard}`}>
            <h2>Memories</h2>
            <p>Read the memories and messages that people have left.</p>
            <span className={styles.linkCardAction}>Read the memories</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
